import { useEffect, useState } from "react";
import { adminOpenAIUsage } from "@/lib/api";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
} from "recharts";
import { Zap, DollarSign, RefreshCcw } from "lucide-react";

function Stat({ label, value, sub }) {
    return (
        <div className="bl-card p-5">
            <div className="bl-eyebrow">{label}</div>
            <div className="mt-2 font-display font-semibold text-2xl text-bl">
                {value}
            </div>
            {sub && (
                <div className="mt-1 text-xs text-bl-muted">{sub}</div>
            )}
        </div>
    );
}

const fmt = (n) => (n || 0).toLocaleString();

export default function OpenAIUsage() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            setLoading(true);
            const r = await adminOpenAIUsage(days);
            setData(r);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [days]);

    if (loading || !data) {
        return (
            <div className="bl-card p-10 text-center text-bl-muted">
                Loading usage telemetry…
            </div>
        );
    }

    const t = data.totals || {};

    return (
        <div className="space-y-5" data-testid="admin-openai-usage">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-bl-primary" />
                    <h2 className="font-display font-semibold text-2xl text-bl">
                        OpenAI usage
                    </h2>
                </div>
                <div className="flex items-center gap-2">
                    {[7, 30, 90, 180].map((d) => (
                        <button
                            key={d}
                            onClick={() => setDays(d)}
                            className={
                                "bl-chip " +
                                (days === d ? "bl-chip-active" : "")
                            }
                            data-testid={`openai-window-${d}d`}
                        >
                            {d}d
                        </button>
                    ))}
                    <button
                        onClick={load}
                        className="bl-btn-ghost text-sm inline-flex items-center gap-1"
                        data-testid="openai-refresh-btn"
                    >
                        <RefreshCcw className="w-4 h-4" /> Refresh
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat
                    label="Calls"
                    value={fmt(t.calls)}
                    sub={`last ${data.window_days}d`}
                />
                <Stat
                    label="Estimated spend"
                    value={`$${(t.estimated_usd || 0).toFixed(2)}`}
                    sub={`@ ${data.pricing_assumption?.model || "gpt-5.2"} rates`}
                />
                <Stat
                    label="Input tokens"
                    value={fmt(t.prompt)}
                    sub={`${fmt(t.cached || 0)} cached`}
                />
                <Stat
                    label="Output tokens"
                    value={fmt((t.completion || 0) + (t.reasoning || 0))}
                    sub={`${fmt(t.reasoning || 0)} reasoning`}
                />
            </div>

            <div className="bl-card p-5">
                <div className="bl-eyebrow mb-3">Tokens per day</div>
                {data.by_day.length === 0 ? (
                    <div className="text-bl-muted text-sm py-8 text-center">
                        No usage in this window yet.
                    </div>
                ) : (
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data.by_day}>
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke="var(--bl-border)"
                                />
                                <XAxis
                                    dataKey="date"
                                    stroke="var(--bl-muted)"
                                    fontSize={11}
                                />
                                <YAxis
                                    stroke="var(--bl-muted)"
                                    fontSize={11}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: "var(--bl-surface)",
                                        border: "1px solid var(--bl-border)",
                                        borderRadius: "8px",
                                    }}
                                />
                                <Bar
                                    dataKey="prompt"
                                    name="Input"
                                    fill="var(--bl-primary)"
                                    stackId="a"
                                />
                                <Bar
                                    dataKey="completion"
                                    name="Output"
                                    fill="var(--bl-accent, #c89a4a)"
                                    stackId="a"
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="bl-card p-5">
                    <div className="bl-eyebrow mb-3">By kind</div>
                    <table className="w-full text-sm">
                        <tbody>
                            {data.by_kind.map((k) => (
                                <tr
                                    key={k.kind}
                                    className="border-b border-bl last:border-0"
                                >
                                    <td className="py-2 text-bl capitalize">
                                        {k.kind}
                                    </td>
                                    <td className="py-2 text-bl-muted text-xs font-mono-bl">
                                        {fmt(k.calls)} calls
                                    </td>
                                    <td className="py-2 text-right font-mono-bl text-xs text-bl-muted">
                                        {fmt(k.total)} tokens
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="bl-card p-5">
                    <div className="bl-eyebrow mb-3 flex items-center gap-2">
                        <DollarSign className="w-3 h-3" />
                        <span>Pricing assumption</span>
                    </div>
                    <ul className="space-y-1.5 text-sm text-bl-muted">
                        <li className="flex justify-between">
                            <span>Input</span>
                            <span className="font-mono-bl">
                                ${data.pricing_assumption?.input_per_1m_usd}/1M
                            </span>
                        </li>
                        <li className="flex justify-between">
                            <span>Cached input</span>
                            <span className="font-mono-bl">
                                $
                                {
                                    data.pricing_assumption
                                        ?.cached_input_per_1m_usd
                                }
                                /1M
                            </span>
                        </li>
                        <li className="flex justify-between">
                            <span>Output (incl. reasoning)</span>
                            <span className="font-mono-bl">
                                ${data.pricing_assumption?.output_per_1m_usd}/1M
                            </span>
                        </li>
                    </ul>
                    <p className="mt-3 text-[11px] text-bl-muted">
                        Tune via{" "}
                        <code className="font-mono-bl">
                            OPENAI_PRICE_INPUT_PER_1M
                        </code>{" "}
                        /{" "}
                        <code className="font-mono-bl">
                            OPENAI_PRICE_OUTPUT_PER_1M
                        </code>{" "}
                        env vars.
                    </p>
                </div>
            </div>
        </div>
    );
}
